<template>
  <div class="search">
    <div class="container">
      <div class="back" @click="toBackPage">
        <arrow-svg class="back-arrow-icon"></arrow-svg> Back
      </div>
      <h1 class="page-title">Search result</h1>

      <div class="search-field">
        <label>
          <input
            class="form-control search-input"
            type="text"
            placeholder="You can search for: module, driver, node, bridge, 12NC, etc"
            v-model="searchValue"
            @keypress.enter="onSearch(searchValue)"
            :disabled="loading"
          />
          <search-svg class="search-icon" @click="onSearch(searchValue)" />
          <cross-svg
            v-if="hasSearchValue"
            class="cross-icon"
            @click="clearSearchValue"
          />
        </label>
        <input
          type="button"
          class="btn btn-secondary"
          value="Search"
          @click="onSearch(searchValue)"
        />
      </div>
      <div v-if="hasResult">
        <span>
          {{ this.searchResults.length }} results for '{{
            this.searchValue
          }}'</span
        >
      </div>
      <div class="divider"></div>
      <div v-if="!hasResult" class="no-search-result">
        <h2>No results...</h2>
        <p>Use the search field above to find your product.</p>
      </div>
      <div v-if="hasResult" class="search-result">
        <div v-for="result in results" :key="result.item" class="product-item">
          <div
            v-if="result.image"
            class="product-image"
            @click="setProduct(result)"
          >
            <img :src="getImageUrl(result.image)" :alt="result.name" />
          </div>
          <div class="product-details">
            <div class="product-details-col">
              <div
                v-if="result.name"
                class="product-name"
                @click="setProduct(result)"
              >
                {{ getProductName(result) }}
              </div>
              <div class="product-devider">//</div>
              <div
                v-if="result.nc12"
                class="product-code"
                @click="setProduct(result)"
              >
                {{ result.nc12 }}
              </div>
            </div>
            <div v-if="result.dataSheetUrl" class="product-details-col">
              <download-svg class="download-icon" />
              <a :href="result.dataSheetUrl" target="_blank" class="view"
                >View datasheet</a
              >
            </div>
          </div>
          <div
            v-if="!resultIsBridge(result) && result.type !== 'Sensor'"
            class="product-arrow"
            @click="setProduct(result)"
          >
            <arrow-svg class="arrow-icon" />
          </div>
        </div>
        <Pagination
          :searchResults="searchResults"
          :activePage="activePage"
          :itemsPerPage="itemsPerPage"
          @setActivePage="setActivePage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import arrowSvg from "@/assets/svg/icons/signify-icons_13-arrow-button-16px.svg?inline";
import searchSvg from "@/assets/svg/icons/signify-icons_26-search-16px.svg?inline";
import downloadSvg from "@/assets/svg/icons/signify-icons_05-download-12px.svg?inline";
import crossSvg from "@/assets/svg/icons/signify-icons_15-cross-16px.svg?inline";
import { mapState } from "vuex";
import { EventBus } from "@/eventbus.js";
import { http } from "@/services/http";

export default {
  name: "Search",
  data() {
    return {
      loading: false,
      searchValue: "",
      activePage: 1,
      itemsPerPage: 12,
      searchResults: [],
    };
  },
  props: {
    searchTerm: {
      type: String,
      default: "",
    },
    back: {
      type: String,
      default: "landing",
    },
  },
  components: {
    arrowSvg,
    searchSvg,
    downloadSvg,
    crossSvg,
  },
  computed: {
    ...mapState("brain", ["region"]),
    hasResult() {
      return this.searchResults.length;
    },
    results() {
      return this.searchResults.slice(
        (this.activePage - 1) * this.itemsPerPage,
        this.activePage * this.itemsPerPage
      );
    },
    backPage() {
      return this.back === this.$route.name ? "" : this.back;
    },
    hasSearchValue() {
      return this.searchValue !== "";
    },
  },
  created() {
    this.$store.commit("brain/ui/showControls", false);
    EventBus.$on("globalSearch", (searchTerm) => {
      this.searchValue = searchTerm;
      this.onSearch(this.searchValue);
    });
  },
  mounted() {
    this.searchValue = this.searchTerm;
    this.onSearch(this.searchValue);
  },
  methods: {
    onSearch(searchValue) {
      if (searchValue) {
        this.loading = true;
        this.$store.commit("brain/global/setBusy", true, { root: true });
        http
          .get(`api/v2/search?searchTerm=` + searchValue)
          .then((response) => {
            this.searchResults = response.data.data;
            // Poor man's solution of hiding Bodine
            this.activePage = 1;
            this.loading = false;
            this.$store.commit("brain/global/setBusy", false, { root: true });
          })
          .catch((err) => {
            console.log(err);
            this.loading = false;
            this.$store.commit("brain/global/setBusy", false, { root: true });
          });
      }
    },
    resultIsBridge(result) {
      return result.type === "Bridge";
    },
    setProduct(result) {
      switch (result.type) {
        case "Driver":
          this.setModuleOrDriver(result);
          break;
        case "Module":
          this.setModuleOrDriver(result);
          break;
        case "Emergency Driver":
          this.setEmergencyDriver(result);
          break;
      }
    },
    setModuleOrDriver(result) {
      this.$store.dispatch("brain/solutions/handleNewSolution", false);
      this.$router.push({
        name: "advanced",
        params: {
          searchItem: { nc12: result.nc12, type: result.type },
          fetchFiltersOnInit: false,
        },
      });
    },
    setEmergencyDriver(result) {
      this.$router.push({
        name: "emergencyDriver",
        params: {
          searchItem: { nc12: result.nc12, type: result.type },
        },
      });
    },
    setActivePage(page) {
      this.activePage = page;
    },
    getImageUrl(image) {
      if (image != null) {
        return image.startsWith("http://") || image.startsWith("https://")
          ? image
          : this.$imageBase + image;
      }
    },
    getProductName(product) {
      if (product.type === "Driver") {
        return this.region === "na" ? product.marketingName : product.name;
      }
      return product.name;
    },
    toBackPage() {
      if (this.backPage === "advanced") {
        this.$store.commit("brain/global/setCheckReload", true);
      }
      this.$router.push(this.backPage);
    },
    clearSearchValue() {
      this.searchValue = "";
    },
  },
};
</script>

<style lang="scss" scoped>
.search {
  flex-grow: 2;

  .page-title {
    margin-bottom: 40px;
    font-family: $top-headings-font-family;
  }

  .back {
    margin-top: 115px;
    margin-bottom: 5px;
    display: block;
    color: $blue-500;
    cursor: pointer;

    .back-arrow-icon {
      width: 16px;
      transform: rotate(180deg);

      path {
        fill: $blue-500;
      }
    }
  }

  .search-field {
    margin-bottom: 60px;
    display: flex;
    flex-flow: row wrap;
    width: 65%;

    input {
      flex: 1 0 0;
    }

    label {
      margin: 0 16px 0 0;
      flex: 4 0 0;
      position: relative;

      .search-input {
        width: 100%;
        padding-left: 36px;
      }

      .search-icon {
        width: 16px;
        position: absolute;
        top: 13px;
        left: 12px;
        cursor: pointer;

        path {
          fill: $dark-gray-800;
        }
      }

      .cross-icon {
        width: 16px;
        position: absolute;
        top: 13px;
        right: 12px;
        cursor: pointer;

        path {
          fill: $dark-gray-800;
        }
      }
    }
  }

  .divider {
    width: 100%;
    border-bottom: solid 1px $mid-gray-200;
    margin-bottom: 24px;
  }

  .search-result {
    margin-bottom: 110px;

    .product-item {
      display: flex;
      align-items: center;
      height: 90px;
      margin: 5px 0;

      .product-image,
      .product-name,
      .product-code,
      .product-arrow {
        cursor: pointer;
      }

      .product-image {
        flex: 0 1 auto;

        img {
          width: 75px;
          margin-right: 24px;
        }
      }

      .product-details {
        flex: 1 0 0;
        display: flex;
        flex-direction: column;

        //safari only css for responsiveness fix
        @media not all and (min-resolution: 0.001dpcm) {
          @supports (-webkit-appearance: none) {
            height: 42px;
          }
        }

        .product-details-col {
          display: flex;
          flex-direction: row;

          .download-icon {
            width: 12px;
            margin-right: 5px;

            //safari only css for responsiveness fix
            @media not all and (min-resolution: 0.001dpcm) {
              @supports (-webkit-appearance: none) {
                height: 21px;
              }
            }

            path {
              fill: $blue-500;
            }
          }

          .view {
            text-decoration: underline;
          }
        }
        .product-devider {
          margin: 0 10px;
        }
      }

      .product-arrow {
        flex: 0 1 auto;
        display: flex;
        justify-content: flex-end;

        .arrow-icon {
          width: 16px;

          path {
            fill: $dark-gray-800;
          }
        }
      }
    }

    .pagination {
      margin-top: 60px;
    }
  }
}
</style>
